import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldCheck, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const KYC = () => {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ id_type: "", id_number: "", id_front_image: "", id_back_image: "", selfie_image: "" });
  const [busy, setBusy] = useState(false);

  const handleFile = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(s => ({ ...s, [key]: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.id_type || !form.id_number || !form.id_front_image || !form.selfie_image) {
      toast.error("Please complete all required fields");
      return;
    }
    setBusy(true);
    try {
      await api.post("/kyc", form);
      await refresh();
      toast.success("Identity verified! You can now send money.");
      setTimeout(() => nav("/dashboard"), 700);
    } catch (err) { toast.error(err.response?.data?.detail || "KYC failed"); }
    finally { setBusy(false); }
  };

  if (user?.kyc_status === "approved") {
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16 text-center" data-testid="kyc-verified">
        <div className="ra-card p-12">
          <CheckCircle2 className="mx-auto text-[var(--ra-success)]" size={48} />
          <h1 className="font-heading text-3xl font-bold mt-4">You're verified</h1>
          <p className="text-[var(--ra-muted)] mt-2">Your account is ready to send money to all 9 supported countries.</p>
          <Button onClick={() => nav("/send")} className="btn-primary rounded-md mt-6" data-testid="kyc-go-send">Send money</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10" data-testid="kyc-root">
      <div className="mb-8 fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-1.5">Verification</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Verify your identity</h1>
        <p className="text-[var(--ra-muted)] mt-2">Required to comply with AML regulations. Takes ~2 minutes.</p>
      </div>

      <form onSubmit={submit} className="ra-card p-6 sm:p-8 space-y-5 fade-up fade-up-2" data-testid="kyc-form">
        <div>
          <Label className="text-sm text-[var(--ra-muted)]">ID type</Label>
          <Select value={form.id_type} onValueChange={(v) => setForm(s => ({ ...s, id_type: v }))}>
            <SelectTrigger className="mt-1.5 h-11 rounded-md" data-testid="kyc-id-type"><SelectValue placeholder="Select ID type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="passport" data-testid="kyc-type-passport">Passport</SelectItem>
              <SelectItem value="national_id" data-testid="kyc-type-national">National ID</SelectItem>
              <SelectItem value="drivers_license" data-testid="kyc-type-license">Driver's License</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-sm text-[var(--ra-muted)]">ID number</Label>
          <Input value={form.id_number} onChange={(e) => setForm(s => ({ ...s, id_number: e.target.value }))} className="mt-1.5 h-11 rounded-md" data-testid="kyc-id-number" />
        </div>

        <FileSlot label="Front of ID" required preview={form.id_front_image} onChange={handleFile("id_front_image")} testid="kyc-front" />
        <FileSlot label="Back of ID (optional)" preview={form.id_back_image} onChange={handleFile("id_back_image")} testid="kyc-back" />
        <FileSlot label="Selfie holding your ID" required preview={form.selfie_image} onChange={handleFile("selfie_image")} testid="kyc-selfie" />

        <Button type="submit" disabled={busy} className="btn-primary w-full h-12 rounded-md" data-testid="kyc-submit-btn">
          <ShieldCheck size={16} className="mr-2" />
          {busy ? "Verifying…" : "Submit for verification"}
        </Button>
      </form>
    </div>
  );
};

const FileSlot = ({ label, preview, onChange, required, testid }) => (
  <div>
    <Label className="text-sm text-[var(--ra-muted)]">{label} {required && <span className="text-[var(--ra-error)]">*</span>}</Label>
    <label className="mt-1.5 block cursor-pointer rounded-md border border-dashed border-[var(--ra-border)] p-4 hover:border-[var(--ra-primary)]/50 transition-colors" data-testid={testid}>
      {preview ? (
        <div className="flex items-center gap-3">
          <img src={preview} alt="upload preview" className="h-16 w-16 rounded-md object-cover" />
          <div className="text-sm text-[var(--ra-success)] flex items-center gap-1.5"><CheckCircle2 size={14} /> File ready · click to replace</div>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-[var(--ra-muted)]">
          <Upload size={20} />
          <span className="text-sm">Click to upload (max 2MB)</span>
        </div>
      )}
      <input type="file" accept="image/*" className="hidden" onChange={onChange} data-testid={`${testid}-input`} />
    </label>
  </div>
);

export default KYC;
